"use client";

import {ComponentType, memo, ReactNode} from "react";

import {Placement} from "@floating-ui/react";

import {type GuestsStepperInstance} from "@/features/rental";

import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons";
import {Typography} from "@/shared/ui/data-display/typography";
import {StepperField} from "@/shared/ui/form/steppers/stepper-field";
import {FloatingDropdown} from "@/shared/ui/overlay/floating-dropdown";

import {useGuestsDropdown} from "./hooks/use-guests-dropdown";
import {GuestsButton, PetsToggle} from "./parts";

import "./styles/guests-view.scss";

export interface GuestsButtonProps {
    totalGuests: number;
    totalPets: number;
    isOpen: boolean;
    onClick: () => void;
}

interface GuestsViewProps {
    model: GuestsStepperInstance;
    label?: string;
    className?: string;
    showResetButton?: boolean;
    showPetsSubtitle?: boolean;
    usePetsToggle?: boolean;
    children?: ReactNode;
    TriggerButton?: ComponentType<GuestsButtonProps>;
    dropdownOffset?: number;
    placement?: Placement;
}

export const GuestsView = memo(function GuestsView({
    model,
    label,
    className,
    showResetButton,
    showPetsSubtitle = false,
    usePetsToggle = true,
    children,
    TriggerButton = GuestsButton,
    dropdownOffset = 8,
    placement,
}: GuestsViewProps) {
    const {snapshot, isOpen, setIsOpen, dropdownRef, handleReset, handleApply, handleToggle} = useGuestsDropdown({model});
    const {t} = useTranslation("search");
    const petsSubtitle = showPetsSubtitle
        ? snapshot.petsNotAllowed
            ? t("$.validation.noPetsAllowed")
            : snapshot.maxPetsAllowed > 0
              ? t("maxDogs", {count: snapshot.maxPetsAllowed})
              : undefined
        : undefined;

    return (
        <div ref={dropdownRef} className={`guests-view ${className || ""}`}>
            <Typography size={14} color="title" as="label">
                {label}
            </Typography>

            <FloatingDropdown
                isOpen={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open);

                    if (!open) model.apply();
                }}
                placement={placement}
                dropdownClassName="guests-view__dropdown"
                offsetValue={dropdownOffset}
                trigger={<TriggerButton totalGuests={snapshot.totalGuests} totalPets={snapshot.totalPets} isOpen={isOpen} onClick={handleToggle} />}
            >
                <div className="guests-view__steppers">
                    <StepperField model={model.adults} label={t("adults")} className="guests-view__stepper" />

                    <StepperField model={model.children} label={t("children")} className="guests-view__stepper" />

                    {usePetsToggle ? (
                        <PetsToggle model={model} subtitle={petsSubtitle} className="guests-view__stepper" />
                    ) : (
                        <StepperField model={model.pets} label={t("dogs")} subtitle={petsSubtitle} className="guests-view__stepper" />
                    )}
                </div>

                {showResetButton && (
                    <>
                        <div className="guests-view__reset">
                            <ButtonBase variant="secondary" onClick={handleReset} size="sm">
                                {t("$.common.clear")}
                            </ButtonBase>

                            <ButtonBase variant="primary" onClick={handleApply} size="sm">
                                {t("$.common.update")}
                            </ButtonBase>
                        </div>
                    </>
                )}
                {children && <div className="guests-view__reset">{children}</div>}
            </FloatingDropdown>
        </div>
    );
});
